import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from './ui/badge';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const LOGO_BASE = 'https://customer-assets.emergentagent.com/job_bim-solutions-1/artifacts/';

const CASES = [
  { name: 'Superior Tribunal Militar (STM)', logo: `${LOGO_BASE}gpc9bbrh_Logo_Superior_Tribunal_Militar.svg.png`, text: 'Implantação e implementação de BIM em órgão público.' },
  { name: 'Amazul', logo: `${LOGO_BASE}4b15oojf_NOVO_LOGO_AMAZUL-1.png`, text: 'Implantação e implementação de BIM em órgão público.' },
  { name: 'SENAI Distrito Federal', logo: `${LOGO_BASE}18x9sz3u_senai.jpg`, text: 'Implantação e implementação de BIM.' },
  { name: 'ADN', logo: `${LOGO_BASE}82tjx8zl_adn.png`, text: 'Implantação e implementação de BIM em construtora de grande porte.' },
  { name: 'BID', logo: `${LOGO_BASE}knq5o72e_BID_logo.png`, text: 'Desenvolvimento do primeiro projeto colaborativo em BIM em 2013, com diversos parceiros. O projeto completo, de aproximadamente 2.000 m², levou apenas 15 dias para ser desenvolvido.' },
  { name: "Bob's", logo: `${LOGO_BASE}tl7s8b09_bobs.png`, text: 'Treinamento do software Revit.' },
  { name: 'Burger King', logo: `${LOGO_BASE}cg1gnzm0_bk.avif`, text: 'Implantação e implementação BIM e projetos executivos dos restaurantes da rede.' },
  { name: 'Polícia Federal', logo: `${LOGO_BASE}blbdefhl_policia%20federal.png`, text: 'Treinamentos do software Revit.' },
  { name: 'Rede Globo', logo: `${LOGO_BASE}l0yw6u2q_rede%20globo.jpg`, text: 'Treinamentos do software Revit.' },
  { name: 'Governo de Santa Catarina', logo: `${LOGO_BASE}5viiio1u_secretaria%20infra%20sc.jpg`, text: 'Treinamento introdutório de BIM para servidores públicos de diversas secretarias do governo.' },
  { name: 'Secretaria de Infraestrutura de Manaus', logo: `${LOGO_BASE}03mzksue_seinfra.jfif`, text: 'Projeto BIM colaborativo.' },
  { name: 'Senac RJ', logo: `${LOGO_BASE}rib2qjyk_SENAC-RIO-750x465.jpg`, text: 'Treinamentos do software Revit.' },
  { name: 'Museu Nacional', logo: `${LOGO_BASE}abt39qng_Logo_-_Museu_Nacional-UFRJ.jpg`, text: 'Coordenação BIM do trabalho de modelagem dos escombros do Museu que havia pegado fogo, preparando o material BIM para a próxima equipe de projetos. Levantamento por nuvem de pontos e migração de projetos existentes para o Revit.' },
  { name: 'Shopping Sul', logo: `${LOGO_BASE}7ot3loql_shopping%20sul.png`, text: 'Modelagem BIM de todos os projetos do Shopping para análise e compatibilização de projetos — empreendimento com mais de 40.000 m².' },
  { name: 'Telmec', logo: `${LOGO_BASE}beux9jtm_telmec.png`, text: 'Treinamento do software Revit.' },
  { name: 'Jotanunes', logo: `${LOGO_BASE}kemmoj4b_jotanunes.png`, text: 'Implantação e implementação BIM em construtora de grande porte.' },
  { name: 'JCGontijo', logo: `${LOGO_BASE}gu56vsjo_jccongtijo.jfif`, text: 'Treinamento do software Revit.' },
];

const CARD_BG = '#26262b';
const ROTATE_MS = 7000;

const ClientsSection = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = CASES.length;

  const goTo = useCallback((i) => setIndex(((i % count) + count) % count), [count]);
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % count), ROTATE_MS);
    return () => clearInterval(t);
  }, [paused, count]);

  const c = CASES[index];

  return (
    <section className="py-8 md:py-10 bg-gradient-to-b from-zinc-900 to-black overflow-hidden" data-testid="clients-section">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <Badge className="bg-red-600/20 text-red-400 border-red-500/30 mb-4">
            Confiança Comprovada
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Clientes que <span className="text-red-500">confiam</span> na Metodologia Justi
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Cases reais de implantação de BIM, projetos e treinamentos em empresas e órgãos públicos
          </p>
        </div>

        <div
          className="relative max-w-5xl mx-auto"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div
            className="relative rounded-2xl border border-zinc-800 overflow-hidden shadow-xl"
            style={{ backgroundColor: CARD_BG }}
          >
            {/* Logo da empresa (direita) */}
            <div className="absolute right-0 top-0 bottom-0 w-[38%] md:w-[32%] hidden sm:flex items-center justify-center p-6">
              <div className="bg-white rounded-2xl w-full h-32 md:h-40 flex items-center justify-center p-5 shadow-lg">
                <img
                  key={c.logo}
                  src={c.logo}
                  alt={c.name}
                  className="max-h-full max-w-full object-contain animate-in fade-in duration-700"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Conteúdo (esquerda) */}
            <div
              key={index}
              className="relative p-7 md:p-12 sm:pr-[40%] md:pr-[34%] min-h-[260px] flex flex-col justify-center animate-in fade-in slide-in-from-bottom-2 duration-500"
            >
              {/* Logo no mobile */}
              <div className="sm:hidden mb-5">
                <div className="bg-white rounded-xl inline-flex items-center justify-center p-4 h-20 min-w-[140px]">
                  <img src={c.logo} alt={c.name} className="max-h-full max-w-[160px] object-contain" loading="lazy" />
                </div>
              </div>

              <div className="mb-6">
                <span className="inline-block border-2 border-orange-500 text-orange-500 rounded-full px-6 py-2 text-xs font-bold tracking-[0.15em] uppercase">
                  Cliente
                </span>
              </div>
              <p className="text-gray-100 text-base md:text-xl leading-relaxed mb-5" data-testid="client-case-text">
                {c.text}
              </p>
              <div className="text-sm md:text-base">
                <span className="font-bold text-white">{c.name}</span>
              </div>
            </div>
          </div>

          {/* Controles */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              type="button"
              onClick={prev}
              aria-label="Cliente anterior"
              data-testid="client-prev"
              className="w-10 h-10 rounded-full border border-zinc-700 text-gray-300 hover:text-white hover:border-orange-500 flex items-center justify-center transition-colors"
            >
              <ChevronLeft size={20} />
            </button>

            <span className="text-sm text-gray-400 tabular-nums min-w-[64px] text-center" data-testid="client-counter">
              {index + 1} / {count}
            </span>

            <button
              type="button"
              onClick={next}
              aria-label="Próximo cliente"
              data-testid="client-next"
              className="w-10 h-10 rounded-full border border-zinc-700 text-gray-300 hover:text-white hover:border-orange-500 flex items-center justify-center transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientsSection;
